import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { siteSwitches } from "../../api/endpoints/siteSwitches";
import { qk } from "../../api/queryKeys";
import type { PortOut } from "../../api/types/siteSwitch";
import { Table, type Column } from "../../components/ui/Table";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { SwitchPortForm } from "./SwitchPortForm";

// Presentational + port mutations. The ports read query is owned by SwitchDetail
// (passed in via `ports`); port writes here invalidate qk.siteSwitchPorts so the
// owner refetches. `options`/`nameByTag` come from useVlanTemplateOptions (owner).
export function SwitchPortMap({
  siteSlug,
  switchSlug,
  ports,
  options,
  nameByTag,
  canWrite,
}: {
  siteSlug: string;
  switchSlug: string;
  ports: PortOut[];
  options: { value: number; label: string }[];
  nameByTag: Map<number, string>;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PortOut | null>(null);
  const [adding, setAdding] = useState(false);

  const del = useMutation({
    mutationFn: (number: number) => siteSwitches.deletePort(siteSlug, switchSlug, number),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteSwitchPorts(siteSlug, switchSlug) });
      void qc.invalidateQueries({ queryKey: qk.siteSwitch(siteSlug, switchSlug) });
    },
  });

  const tagName = (t: number) => nameByTag.get(t) ?? String(t);
  const columns: ReadonlyArray<Column<PortOut>> = [
    { key: "number", header: "#", width: 48 },
    { key: "name", header: "Name", render: (p) => p.name ?? "—" },
    { key: "mode", header: "Mode" },
    { key: "native", header: "Native", render: (p) => (p.native_vlan_tag == null ? "—" : tagName(p.native_vlan_tag)) },
    {
      key: "tagged",
      header: "Tagged",
      render: (p) => (p.tagged_vlan_tags.length ? p.tagged_vlan_tags.map(tagName).join(", ") : "—"),
    },
    { key: "notes", header: "Notes", render: (p) => p.notes ?? "—" },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (p) =>
        canWrite ? (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <button type="button" style={linkBtn} onClick={() => setEditing(p)}>Edit</button>
            <button
              type="button"
              style={dangerLinkBtn}
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(`Delete port ${p.number}?`)) del.mutate(p.number);
              }}
            >
              Delete
            </button>
          </span>
        ) : null,
    },
  ];

  const rows = [...ports].sort((a, b) => a.number - b.number);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ margin: 0, fontSize: "0.9rem" }}>Port map</h4>
        {canWrite && (
          <button type="button" style={primaryBtn} onClick={() => setAdding(true)}>Add port</button>
        )}
      </div>

      {del.error && <ErrorBanner error={del.error} title="Delete failed" />}

      <Table<PortOut>
        columns={columns}
        rows={rows}
        rowKey={(p) => String(p.number)}
        empty={<EmptyState title="No ports" description="Add the ports this switch trunks/accesses." />}
      />

      <Modal
        open={adding || editing !== null}
        onClose={() => { setAdding(false); setEditing(null); }}
        title={editing ? `Edit port ${editing.number}` : "Add port"}
      >
        <SwitchPortForm
          siteSlug={siteSlug}
          switchSlug={switchSlug}
          port={editing ?? undefined}
          options={options}
          canWrite={canWrite}
          onClose={() => { setAdding(false); setEditing(null); }}
        />
      </Modal>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
};
const linkBtn: React.CSSProperties = {
  background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: "0.85rem", padding: 0,
};
const dangerLinkBtn: React.CSSProperties = { ...linkBtn, color: "var(--crit)" };
